import Constants from '@/constants'

/**
 * Prevents a user from copy and pasting an existing
 * transaction into a new tab or window. This is achieved
 * using the BroadcastChannel API. We send a PING to other
 * browsers, and if we receive a PONG back from a browser,
 * then we know the transaction is open in another tab.
 */
export const duplicateTabMixin = {
  data() {
    return {
      channel: null,
    }
  },
  created: function () {
    this.setupBroadcastChannel()
  },
  updated: function () {
    this.setupBroadcastChannel()
  },
  beforeDestroy() {
    this.cleanupChannel()
  },
  methods: {
    /**
     * Initializes a new broadcast channel and subscribes
     * to PING and PONG event listeners.
     */
    setupBroadcastChannel: async function () {
      // If the browser doesn't support BroadcastChannel,
      // then safely just exit out
      if (!('BroadcastChannel' in self)) {
        return
      }

      // If we already have an existing channel,
      // then don't make another one
      if (this.channel) {
        return
      }

      // If TNX ID does not exist in the query
      // then we can't really broadcast, so exit out
      const transactionId = this.$route.query.transactionId
      if (!transactionId) {
        return
      }

      this.channel = new BroadcastChannel('tab')

      // Post a PING to other tabs and check if we get a pong back
      this.sendPing(transactionId)

      this.channel.onmessage = async (event) => {
        const { action, payload } = event.data

        // If we receive a PING from another tab with the given TNX ID
        // we should respond with a pong back to them
        if (action === 'ping' && payload.transactionId === transactionId) {
          this.sendPong(transactionId)
        }

        // If we got a PONG from another tab then that means there is
        // already a TNX in progress for the given TNX ID so exit out
        if (action === 'pong' && payload.transactionId === transactionId) {
          this.channel.close()

          this.$logInfo({
            transactionId: transactionId,
            message: Constants.logMessages.duplicateTab,
          })

          // We can't use vue-router here because there are race conditions
          // with other vue-router instances pushing routes, and this
          // must always be #1 priority
          window.location = 'error'
        }
      }
    },
    /**
     * Sends a ping broadcast to other tabs.
     * @param {string} transactionId The ID of the transaction to send along with the ping
     */
    sendPing: function (transactionId) {
      this.channel.postMessage({
        action: 'ping',
        payload: { transactionId },
      })
    },
    /**
     * Sends a pong broadcast to other tabs.
     * @param {string} transactionId The ID of the transaction to send along with the pong
     */
    sendPong: function (transactionId) {
      this.channel.postMessage({
        action: 'pong',
        payload: { transactionId },
      })
    },
    /**
     * Tears down the channel and closes event listeners.
     */
    cleanupChannel: function () {
      if (this.channel) {
        this.channel.close()
      }
      this.channel = undefined
    },
  },
}
